/**
 * Jobs this BROWSER started — the list the render-ready toast watches.
 *
 * A user who kicks off a render walks away from the job screen; ten minutes
 * later the only thing that tells them it finished is a toast, and the toast
 * needs to know which ids are "mine". The server can't answer that (every
 * session sees every job), so the answer lives in `localStorage` under
 * `ql-tracked-jobs` — a newest-first array of job ids, capped so an active
 * operator can't grow an unbounded poll list.
 *
 * Every function takes the store as an argument rather than reaching for the
 * global: it makes the contract testable in the node test environment (no
 * DOM), and it is the only reason these helpers can live in `web/src/lib`,
 * which the root vitest suite runs.
 *
 * EVERY access is wrapped: `localStorage` throws outright in Safari private
 * mode, with site data blocked, and in a null-origin iframe. A tracked-jobs
 * jar is a convenience — it must never take the app shell down, so a
 * throwing store degrades to "nothing is tracked" (`[]` / no-op).
 */

// Type-only: erased at build time, so this module stays DOM-free and the
// root vitest node suite can import it without pulling api.ts's graph in.
import type { JobStatus } from "../api";

/**
 * Is this status the END of a job, and which way did it end?
 *
 * `"ready"` and `"failed"` are the obvious two. `"incomplete"` is the third
 * and the one that is easy to miss: the server stamps it on a persisted job
 * that has no video once the in-memory job is gone (a restart or redeploy
 * mid-render — `src/server/jobStore.ts`), and the rest of the codebase
 * already treats it as finished (the "in flight" filter in
 * `src/server/index.ts` excludes it). A watcher that only knew `ready` and
 * `failed` would poll such an id forever and never tell the user their
 * render died with the container.
 *
 * From the user's chair "incomplete" is a failure — there is no video and
 * nothing more is coming — so it reports as one.
 *
 * `null` ⇒ still in flight.
 */
export function terminalKind(status: JobStatus | string): "ready" | "failed" | null {
  if (status === "ready") return "ready";
  if (status === "failed" || status === "incomplete") return "failed";
  return null;
}

/** The slice of `Storage` these helpers use. Structural, so the real
 *  `window.localStorage` satisfies it and a test can pass a Map. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const TRACKED_JOBS_KEY = "ql-tracked-jobs";

/** Polling cost is `MAX_TRACKED_JOBS` × one `GET /api/jobs/:id` every 15 s.
 *  20 keeps a heavy day's work watchable without turning the shell into a
 *  load generator. */
export const MAX_TRACKED_JOBS = 20;

/** Newest-first, de-duplicated, capped. Shared by every path so a jar
 *  hand-edited (or written by an older build) is normalised on read. */
function normalise(ids: unknown[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    if (typeof raw !== "string") continue;
    const id = raw.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_TRACKED_JOBS) break;
  }
  return out;
}

/** The ids this browser is watching, newest first. `[]` on anything
 *  unexpected — a missing key, unparseable JSON, a non-array root, or a
 *  storage that throws. */
export function list(storage: StorageLike): string[] {
  try {
    const raw = storage.getItem(TRACKED_JOBS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return normalise(parsed);
  } catch {
    return [];
  }
}

function write(storage: StorageLike, ids: string[]): string[] {
  try {
    if (ids.length === 0) storage.removeItem(TRACKED_JOBS_KEY);
    else storage.setItem(TRACKED_JOBS_KEY, JSON.stringify(ids));
    return ids;
  } catch {
    // The store refused the write (private mode, blocked site data, quota).
    // Report what is ACTUALLY tracked — re-reading, which is `[]` on a store
    // that throws on read too — rather than an optimistic list the next page
    // load would contradict.
    return list(storage);
  }
}

/** Start watching `id`. Re-tracking an existing id moves it to the front
 *  rather than duplicating it; the oldest falls off at the cap. */
export function track(storage: StorageLike, id: string): string[] {
  const clean = typeof id === "string" ? id.trim() : "";
  if (!clean) return list(storage);
  return write(storage, normalise([clean, ...list(storage)]));
}

/** Stop watching `id` (it reached a terminal status, or its toast was
 *  shown). Removing the last id drops the key entirely so an idle browser
 *  leaves nothing behind. */
export function untrack(storage: StorageLike, id: string): string[] {
  const clean = typeof id === "string" ? id.trim() : "";
  const next = list(storage).filter((x) => x !== clean);
  return write(storage, next);
}

// ---------------------------------------------------------------------------
// Remembered job titles
// ---------------------------------------------------------------------------

/**
 * A job carries no human title until the pipeline writes one, and the
 * render-ready toast can fire well before that — so it fell back to the slug
 * ("test-speaker--french-uniform-fixture failed"). Whoever STARTS a job often
 * knows the real title (the sample picker does), so it stashes it here and the
 * toast reads it back.
 *
 * Per-tab `sessionStorage`, best-effort: a browser that refuses site data just
 * gets the old slug behaviour, which is why every access is wrapped.
 */
const JOB_TITLE_PREFIX = "ql-job-title:";

export function rememberJobTitle(id: string, title: string): void {
  const cleanId = typeof id === "string" ? id.trim() : "";
  const cleanTitle = typeof title === "string" ? title.trim() : "";
  if (!cleanId || !cleanTitle) return;
  try {
    window.sessionStorage.setItem(`${JOB_TITLE_PREFIX}${cleanId}`, cleanTitle);
  } catch {
    /* private mode / blocked site data — the slug is still a usable name */
  }
}

/** The remembered title for `id`, or null. */
export function readJobTitle(id: string): string | null {
  const cleanId = typeof id === "string" ? id.trim() : "";
  if (!cleanId) return null;
  try {
    return window.sessionStorage.getItem(`${JOB_TITLE_PREFIX}${cleanId}`)?.trim() || null;
  } catch {
    return null;
  }
}

/** Forget it once the job has reached a terminal state and been reported. */
export function forgetJobTitle(id: string): void {
  const cleanId = typeof id === "string" ? id.trim() : "";
  if (!cleanId) return;
  try {
    window.sessionStorage.removeItem(`${JOB_TITLE_PREFIX}${cleanId}`);
  } catch {
    /* nothing to clean up */
  }
}
